/**
 * Event photo storage — IndexedDB only, never the server.
 *
 * Same hard constraint as child photos: photos of family activities
 * never leave the device. Stored in a dedicated IDB database keyed
 * by "{suggestionId}:{index}" so each event can have multiple photos.
 *
 * The index is a monotonically increasing counter per event — we
 * read the current max and increment. Deletion removes individual
 * photos or all photos for an event.
 */

import { createStore, del, get, keys, set } from "idb-keyval";

const EVENT_PHOTO_STORE = createStore("mic-event-photos-db", "event-photos");

function photoKey(suggestionId: string, index: number): string {
  return `${suggestionId}:${index}`;
}

function parseSuggestionId(key: string): string {
  return key.split(":")[0] ?? key;
}

export async function addEventPhoto(
  suggestionId: string,
  blob: Blob,
): Promise<string> {
  const existing = await getEventPhotoKeys(suggestionId);
  const maxIndex = existing.reduce((max, k) => {
    const parts = k.split(":");
    const idx = Number.parseInt(parts[1] ?? "0", 10);
    return idx > max ? idx : max;
  }, -1);
  const newKey = photoKey(suggestionId, maxIndex + 1);
  await set(newKey, blob, EVENT_PHOTO_STORE);
  return newKey;
}

export async function getEventPhotoKeys(
  suggestionId: string,
): Promise<string[]> {
  const allKeys = await keys<string>(EVENT_PHOTO_STORE);
  return allKeys
    .filter((k) => typeof k === "string" && parseSuggestionId(k) === suggestionId)
    .sort();
}

export async function getEventPhotoBlob(key: string): Promise<Blob | null> {
  const value = await get<Blob>(key, EVENT_PHOTO_STORE);
  return value ?? null;
}

export async function deleteEventPhoto(key: string): Promise<void> {
  await del(key, EVENT_PHOTO_STORE);
}

export async function deleteAllEventPhotos(
  suggestionId: string,
): Promise<void> {
  const photoKeys = await getEventPhotoKeys(suggestionId);
  await Promise.all(photoKeys.map((k) => del(k, EVENT_PHOTO_STORE)));
}

export async function getEventPhotoCount(
  suggestionId: string,
): Promise<number> {
  const photoKeys = await getEventPhotoKeys(suggestionId);
  return photoKeys.length;
}
